/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/
/* eslint-disable no-unused-expressions */

import React, { useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import Switch from 'react-switch';
import { Field, Form, Formik, FieldArray } from 'formik';
import MasksHelper from 'utils/src/masks';
import * as yup from 'yup';
import IMask from 'imask';
import { GlobalLoaderContext } from 'store/context';
import { GlobalAuthContext } from 'store/context';
import { Layout } from 'components';
import ClaimsSummary from 'components/ClaimsSummary';
import ContentCard from 'components/ContentCard';
import { Col, Row } from 'components/Grid';
import Loader from 'components/Loader';
import FormContainer from 'components/FormContainer';
import Button, { ButtonContainer, ButtonContent } from 'components/Button';
import { Label } from "components/Form";
import InputField from 'components/Form/InputField';
import SearchField from 'components/Form/SearchField';
import Colors from 'constants/Colors';
import { FitinsurAPI, MessageCenter } from 'utils';
import {
  BorderSwitch,
  Text,
  Line,
} from './styles';
import HasPermission from 'components/HasPermission/index';
import { LostCancelButton } from 'components/index';

export default function ClaimComposition(props) {
  const maskHelper = new MasksHelper();
  const loading = useContext(GlobalLoaderContext);
  const authContext = useContext(GlobalAuthContext);
  const history = useHistory();
  const formikRef = useRef(null);
  const fitinsur = new FitinsurAPI();
  const mc = new MessageCenter();
  const [facilities, setFacilities] = useState([]);
  const [shouldGenerateLpanFdo, setShouldGenerateLpanFdo] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState("");
  const [maxValuePerc, setMaxValuePerc] = useState(100);
  const [maxValueAmount, setMaxValueAmount] = useState(null);
  const [formOptions, setFormOptions] = useState({
    currencies: [],
  });
  const [operation, setOperation] = useState(null);
  const [validation, setValidation] = useState(yup.object().shape({}));
  const [isBound, setIsBound] = useState(false);
  const [summary, setSummary] = useState({});
  const [searchList, setSearchList] = useState([]);
  const coluns = [
    {
      title: "Insurer Legal Entity",
      value: "nmInsurer"
    },
    {
      title: "Line Size",
      value: "lineSize"
    },
    {
      title: "Amount",
      value: "amount"
    },
    {
      title: "OSND",
      value: "osnd"
    },
  ];

  var mask = {
    mask: Number,
    scale: 2,  // digits after point, 0 for integers
    signed: true,  // disallow negative
    thousandsSeparator: ',',  // any single char
    padFractionalZeros: true,  // if true, then pads zeros at end to the length of scale
    normalizeZeros: false,  // appends or removes zeros at ends
    radix: '.',  // fractional delimiter
    min: 0,
  };

  var maskPercentage = {
    mask: Number,
    scale: 5,  // digits after point, 0 for integers
    signed: true,  // disallow negative
    thousandsSeparator: ',',  // any single char
    padFractionalZeros: true,  // if true, then pads zeros at end to the length of scale
    normalizeZeros: false,  // appends or removes zeros at ends
    radix: '.',  // fractional delimiter
    min: 0,
  };

  function handleSummary() {
    fitinsur.setAuthToken(authContext.token);
    fitinsur.requestHeaderClaims(props.match?.params?.idOperation)
      .then(response => response.json()).then(data => {
        setSummary({
          claimsReferenceNumber: data.ret_value.claimsReferenceNumber,
        });
      }).catch((error) => {
        mc.error(`There was an error while fetching your data: ${handleSummary.name}\n` + error);
        console.error('fitPostSummary - Error:', error);
      });
  };

  const createValidation = () => {
    let validationSchema = {}
    let validationFacility = {}
    let validationInsurer = {}

    for (let f = 0; f < facilities.length; f++) {
      for (let i = 0; i < facilities[f].insurers.length; i++) {
        validationInsurer[`idPersonLegalEntity`] = yup.string().required("Insurer does not have a legal entity selected");
      }
    }
    const osndIndexes = []
    for (let f = 0; f < facilities.length; f++) {
      validationFacility[`claimCurrency`] = yup.string().required("* Required field");
      validationFacility[`claimAmount`] = yup.string().required("* Required field");

      for (let i = 0; i < facilities[f].insurers.length; i++) {
        const currentElement = facilities[f].insurers[i];

        if (currentElement.legalEntity && currentElement?.legalEntity?.lloydsType) {
          validationInsurer[`percLineSize`] = yup.string().required("* Required field");
          validationInsurer[`amount`] = yup.string().required("* Required field");
          if (operation?.idDomainStatus === 250) {
            validationInsurer[`osnd`] = yup.string();
            osndIndexes.push(i)
          }
        } else {
          validationInsurer[`percLineSize`] = yup.string().required("* Required field");
          validationInsurer[`amount`] = yup.string().required("* Required field");
        }
      }

      validationFacility.insurers = yup.array().of(yup.object().shape(validationInsurer))
        .test({
          name: 'first-and-last',
          message: "* Required field",
          test: val =>
            val.every(
              ({ idPersonLegalEntity, percLineSize, amount, osnd }, index) => {
                if (osndIndexes.includes(index)) {
                  return !!percLineSize && !!amount && !!osnd;
                }
                return !!idPersonLegalEntity && !!percLineSize && !!amount;
              }
            )
        })

      validationFacility.insurers = yup.array().of(yup.object().shape(validationInsurer)).test({
        name: 'insurer-legal-entity',
        message: "Insurer does not have a legal entity selected",
        test: val => val.every(({ idPersonLegalEntity }, index) => {
          return !!idPersonLegalEntity;
        })
      })

    }

    validationSchema.facilities = yup.array().of(yup.object().shape(validationFacility))
    setValidation(yup.object().shape(validationSchema))
  }

  const goToLoginPage = () => history.push('/login');

  const handleGetFormOptions = () => {
    fitinsur.setAuthToken(authContext.token);
    return fitinsur.requestGetClaimFormOptions()
      .then(response => response.json())
      .then(response => {
        if (response.ret_code !== 200) {
          mc.error(response.ret_msg_details);
          return;
        }

        setFormOptions(response.ret_value)
      })
      .catch((error) => {
        loading.stop()
        mc.error(`There was an error while fetching your data: ${handleGetFormOptions.name}\n` + error);
      });
  }


  async function requestInfoClaim() {
    loading.start()
    await handleGetFormOptions();

    fitinsur.setAuthToken(authContext.token);
    fitinsur.requestGetClaimInfo(props.match?.params?.idOperation)
      .then(response => response.json()).then(data => {
        setFacilities(data.ret_value.facilities);
        setReferenceNumber(data.ret_value.referenceNumber);
        setShouldGenerateLpanFdo(data.ret_value.shouldGenerateLpanFdo);
        setOperation(data.ret_value.operation);

        formikRef.current?.setFieldValue('facilities', data.ret_value?.facilities.map(item => {
          return {
            ...item,
            facilityName: item.facility_name,
            claimType: item.claimType,
          }
        }));

        formikRef.current?.setFieldValue('lock', data.ret_value?.isLocked);
        loading.stop();
      }).catch((error) => {
        mc.error(`There was an error while fetching your data: ${requestInfoClaim.name}\n` + error);
        console.error('fitPostSummary - Error:', error);
      });
  };

  function generateFDOLpans() {
    loading.start();

    fitinsur.setAuthToken(authContext.token);
    fitinsur.requestGenerateFDOLpans(props.match?.params?.idOperation)
      .then(response => response.json()).then(data => {
        loading.stop();
        if (data.ret_code === 200) return history.push(`/claim-fdo/${props.match?.params?.idOperation}`);
        mc.error("There was an error while fetching your request");
      }).catch((error) => {
        loading.stop();
        mc.error("There was an error while fetching your request");
        console.error('fitPostSummary - Error:', error);
      });
  };

  const handleLock = (isLocked, values) => {
    try {

      let facilities = JSON.parse(JSON.stringify(values.facilities));
      let hasNoLegalEntity = false;
      facilities = facilities.map(facility => {
        facility.claimAmount = parseFloat(removeMask(facility.claimAmount));
        facility.reserveAmount = parseFloat(removeMask(facility.reserveAmount));

        facility.insurers = facility.insurers.map(insurer => {
          if (!insurer.legalEntity || !insurer.legalEntity.personLastName) {
            hasNoLegalEntity = true;
          }

          insurer.amount = parseFloat(removeMask(insurer.amount));
          insurer.percLineSize = parseFloat(removeMask(insurer.percLineSize, true));
          return insurer;
        });

        return facility;
      });
      console.log(hasNoLegalEntity)
      if (hasNoLegalEntity){
        loading.stop();
        return mc.error("Insurer does not have a legal entity selected");
      } 
      loading.start();
      let body = {
        idOperation: parseInt(props.match.params.idOperation),
        isLocked,
        facilities,
        referenceNumber
      };

      fitinsur.setAuthToken(authContext.token);
      fitinsur.requestUpdateClaimComposition(body)
        .then(x => x.json()).then(res => {
          if (res.ret_code !== 200) {
            mc.error(res.ret_msg_details);
            return;
          }

          mc.success("Registration successful!");
          loading.stop();
          if (formikRef.current) {
            formikRef.current?.setFieldValue('lock', isLocked);
          }
        }).catch(e => {
          loading.stop();
          mc.error(e.message);
          return;
        });

    } catch (err) {
      console.log('Error on lock Line Size', err);
    }
  };

  useEffect(() => {
    if (authContext.status !== true) {
      mc.error("User is not authenticated, please Login");
      loading.stop();
      goToLoginPage();
      return;
    } else {
      fitinsur.setAuthToken(authContext.token);
      loading.stop();
    };
    requestInfoClaim();
    handleSummary();
  }, []);

  useEffect(() => {
    setTimeout(function () {
      if (formikRef?.current?.values?.facilities) {
        getPercLnSizeByFacility(true);
      }
    }, 1500);
    createValidation();
  }, [facilities])

  useEffect(() => {
    createValidation();
    handleOperationInfo();
  }, [operation])

  function removeMask(value, isPercentage) {
    if (typeof value === 'number' || !value) return value;

    let maskCurrency = IMask.createMask(isPercentage ? maskPercentage : mask);
    maskCurrency.resolve(value);
    var valueUnformated = maskCurrency.unmaskedValue;
    return parseFloat(valueUnformated);
  }

  function format(value) {
    let maskCurrency = IMask.createMask(mask);
    let formated = maskCurrency.resolve(value ? value.toString() : "0");
    return formated;
  }

  function getAmountMaxValue(facility, facilityIndex, index, claimAmount) {
    let soma = 0;
    let somaLabel = 0;
    facility.insurers.map((item, insurerIndex) => {
      if (index === insurerIndex) return;
      if (!item.amount) return;
      
      soma = (soma + parseFloat(removeMask(item.amount)));
      if (typeof item.percLineSize === 'number') {
        somaLabel = (somaLabel + parseFloat(removeMask(item.amount)));
      } else {
        somaLabel = (somaLabel + parseFloat(removeMask(item.amount)));
      }
    });

    somaLabel = parseFloat(Number(somaLabel).toFixed(2))
    if (isNaN(somaLabel)) somaLabel = 0;
    setMaxValueAmount(parseFloat((removeMask(claimAmount) - somaLabel).toFixed(2)));
    return somaLabel;
  }

  function getLineSizeMaxValue(facility, facilityIndex, index) {
    let soma = 0;
    let somaLabel = 0;
    facility.insurers.map((item, insurerIndex) => {
      if (index === insurerIndex) return;
      if (!item.percLineSize) return;

      soma = (soma + parseFloat(removeMask(item.percLineSize, true)));
      if (typeof item.percLineSize === 'number') {
        somaLabel = (somaLabel + parseFloat(removeMask(item.percLineSize, true)));
      } else {
        somaLabel = (somaLabel + parseFloat(removeMask(item.percLineSize, true)));
      }
    });

    if (isNaN(somaLabel)) somaLabel = 0;
    setMaxValuePerc(100 - somaLabel);

    return somaLabel;
  };

  function getPercentageLnSizeTotalByFacility(facility, facilityIndex) {
    let somaLabel = 0;
    formikRef.current.values.facilities[facilityIndex].insurers.map((item) => {
      if (!item.percLineSize) return;

      if (typeof item.percLineSize === 'number') {
        somaLabel = (somaLabel + parseFloat(removeMask(item.percLineSize, true)));
      } else {
        somaLabel = (somaLabel + parseFloat(removeMask(item.percLineSize, true)));
      }
    });

    if (formikRef.current) {
      formikRef.current?.setFieldValue(`facilities.${facilityIndex}.percLineSizeTotal`, somaLabel);
    }

    return somaLabel;
  };

  function getTotalAmountByFacility(facility, facilityIndex) {
    let soma = 0;
    let somaLabel = 0;
    formikRef.current.values.facilities[facilityIndex].insurers.map((item) => {
      if (!item.percLineSize) return;
      
      soma = (soma + parseFloat(removeMask(item.amount)));
      if (typeof item.percLineSize === 'number') {
        somaLabel = (somaLabel + parseFloat(removeMask(item.amount)));
      } else {
        somaLabel = (somaLabel + parseFloat(removeMask(item.amount)));
      }
      somaLabel = parseFloat(Number(somaLabel).toFixed(2))
    });

    if (formikRef.current) {
      formikRef.current?.setFieldValue(`facilities.${facilityIndex}.totalAmount`, somaLabel);
    }

    return somaLabel;
  };

  async function getPercLnSizeByFacility(initial) {
    let soma = 0;
    formikRef.current.values.facilities.forEach(async (facility, indexFacility) => {
      facility.insurers.forEach((insurer, indexInsurer) => {
        soma += parseFloat(removeMask(insurer.amount, true));

      });
    });
    formikRef.current.values.facilities.forEach(async (facility, indexFacility) => {
      facility.insurers.forEach((insurer, indexInsurer) => {
        if (!insurer.percLineSize) {
          if (formikRef.current) {
            formikRef.current?.setFieldValue(`facilities.${indexFacility}.insurers.${indexInsurer}.percLineSize`, 0);
          }
          return;
        }
        if (initial) {
          let amountUnmask;
          if (typeof insurer.amount === 'number') {
            amountUnmask = parseFloat(removeMask(insurer.amount, true));
          } else {
            amountUnmask = parseFloat(removeMask(insurer.amount, true));
          }
          let somaUnmask = soma / 100;
          const lineSize = (amountUnmask / somaUnmask);
          formikRef.current?.setFieldValue(`facilities.${indexFacility}.insurers.${indexInsurer}.percLineSize`, maskHelper.resolveMask(lineSize, maskHelper.getPercentage(authContext.region, 5, "100.00", "0", true)));
        } else {
          let lineSizePercentageUnmask;
          if (typeof insurer.percLineSize === 'number') {
            lineSizePercentageUnmask = parseFloat(removeMask(insurer.percLineSize, true));
          } else {
            lineSizePercentageUnmask = parseFloat(removeMask(insurer.percLineSize, true));
          }

          //ClaimAmount
          let claimAmount = 0;
          if (facility.claimAmount) {
            if (typeof insurer.percLineSize === 'number') {
              claimAmount = parseFloat(removeMask(facility.claimAmount));
            } else {
              claimAmount = parseFloat(removeMask(facility.claimAmount));
            }
          }

          const amount = (lineSizePercentageUnmask / 100) * claimAmount;
          formikRef.current?.setFieldValue(`facilities.${indexFacility}.insurers.${indexInsurer}.amount`, format(amount));
        }
      });

      setTimeout(() => getPercentageLnSizeTotalByFacility(facility, indexFacility), 200);
      setTimeout(() => getTotalAmountByFacility(facility, indexFacility), 200);
    });

  }

  async function getPercLineSizeByAmount() {
    formikRef.current.values.facilities.forEach(async (facility, indexFacility) => {
      facility.insurers.forEach((insurer, indexInsurer) => {
        let claimAmount = 0;
        if (facility.claimAmount) {
          if (typeof insurer.percLineSize === 'number') {
            claimAmount = parseFloat(removeMask(facility.claimAmount));
          } else {
            claimAmount = parseFloat(removeMask(facility.claimAmount));
          }
        }

        const totalPercLineSize = (parseFloat(removeMask((insurer.amount))) / claimAmount) * 100;
        if (formikRef.current) {
          formikRef.current?.setFieldValue(`facilities.${indexFacility}.insurers.${indexInsurer}.percLineSize`, maskHelper.resolveMask(totalPercLineSize, maskHelper.getPercentage(authContext.region, 5, "100.00", "0", true)))
        }
      });

      setTimeout(() => getTotalAmountByFacility(facility, indexFacility), 200)
      setTimeout(() => getPercentageLnSizeTotalByFacility(facility, indexFacility), 200)
    });
  }

  const validateValues = (values) => {
    for (const facility of values) {
      if (facility.percLineSizeTotal && facility.totalAmount && facility.claimAmount) {
        console.log("facility.percLineSizeTotal", facility.percLineSizeTotal, parseFloat(facility.percLineSizeTotal.toFixed(5)), removeMask(facility.totalAmount), removeMask(facility.claimAmount))
        if (parseFloat(facility.percLineSizeTotal.toFixed(5)) <= 100 && removeMask(facility.totalAmount.toFixed(2)) >= ((removeMask(facility.claimAmount) % 1) - 0.01 + Math.trunc(removeMask(facility.claimAmount))) ) {
          return true;
        }
      }
    }

    return false;
  }

  async function handleOperationInfo() {
    loading.start()
    fitinsur.setAuthToken(authContext.token);
    fitinsur.requestOperationInfo(props.match.params.idOperation)
      .then(response => response.json())
      .then(response => {
        setIsBound(response?.isBound);
        loading.stop();
      })
      .catch((error) => {
        mc.error(`There was an error while fetching your data: ${handleOperationInfo.name}\n` + error);
        loading.stop();
      });
  }

  const searchRequest = (searchValue) => {
    try {
      fitinsur.setAuthToken(authContext.token);
      fitinsur.requestPersonList('fitGetCurrencies', {
        id: 'fitGetCurrencies',
        personName: searchValue,
        idProduct: operation.idProduct,
        idPerson: null,
        idOperationParent: operation.idOperationParent
      }).then((response) => response.json())
        .then((data) => {
          if (data.ret_code !== 200) {
            mc.error(data.ret_msg_details);
            return;
          }

          const currencies = data.ret_value.map(currency => ({
            value: currency.currencyCode,
            id: currency.idCurrency
          }));

          setSearchList(currencies);

          return;
        });
    } catch (err) {
      mc.error(`There was an error while fetching your data: ${searchRequest.name}\n` + err);
    }
  }

  return (
    <Layout idOperation={props.match?.params?.idOperation} title={summary.claimsReferenceNumber || ""} subtitle={null}>
      <Formik
        innerRef={formikRef}
        initialValues={{ lock: false, facilities: [] }}
        onSubmit={() => { }}
        validationSchema={validation}
      >
        {formikProps => {
          return (
            <>
              <ClaimsSummary stage={2} idOperation={props.match?.params?.idOperation} />
              <ContentCard title="CLAIM COMPOSITION" icon info={{ idOperation: props.match?.params?.idOperation || null, idProduct: props.location?.state?.idProduct || null }}>
                <Form>
                  {formikProps.values.facilities.map((facility, index) => {
                    return (
                      <FormContainer show={true} title="Claim Details" key={index}>
                        <>
                          <Row>
                            <Col lg={4} md={4} sm={4} xs={12}>
                              <Field
                                label='Facility Name'
                                name={`facilities[${index}].facilityName`}
                                value={formikProps.values[`facilities_${facility}_facility`]}
                                options={[]}
                                component={InputField}
                                disabled
                              />
                            </Col>
                            <Col lg={4} md={4} sm={4} xs={12}>
                              <Field
                                label='Currency'
                                name={`facilities[${index}].currency`}
                                value={formikProps.values[`facilities_${facility}_currency`]}
                                component={InputField}
                                disabled
                              />

                            </Col>
                            <Col lg={4} md={4} sm={4} xs={12}>
                              <Field
                                label='Claim Type'
                                name={`facilities[${index}].claimType`}
                                value={formikProps.values[`facilities_${facility}_claimType`]}
                                options={[]}
                                component={InputField}
                                disabled
                              />
                            </Col>
                          </Row>

                          <Row>
                            <Col lg={3} md={6} sm={6} xs={12}>
                              <Field
                                label='Default Date'
                                name={`facilities[${index}].defaultDate`}
                                value={formikProps.values[`facilities_${facility}_defaultDate`]}
                                component={InputField}
                                disabled
                              />
                            </Col>
                            <Col lg={3} md={6} sm={6} xs={12}>
                              <Field
                                label='Limitation Period'
                                name={`facilities[${index}].limitationPeriod`}
                                value={formikProps.values[`facilities_${facility}_limitationPeriod`]}
                                component={InputField}
                                disabled
                              />
                            </Col>
                            <Col lg={3} md={6} sm={6} xs={12}>
                              <Field
                                label='Loss Adj. Appointment Date'
                                name={`facilities[${index}].appointmentDate`}
                                value={formikProps.values[`facilities_${facility}_appointmentDate`]}
                                component={InputField}
                                disabled
                              />
                            </Col>
                            <Col lg={3} md={6} sm={6} xs={12}>
                              <Field
                                label='End of Waiting Period'
                                name={`facilities[${index}].endWaitingPeriod`}
                                value={formikProps.values[`facilities_${facility}_endWaitingPeriod`]}
                                component={InputField}
                                disabled
                              />
                            </Col>
                          </Row>
                          <Row>
                            <Col lg={3} md={6} sm={6} xs={12}>
                              <Field
                                label='Claim Currency'
                                name={`facilities.${index}.claimCurrency`}
                                value={formikProps.values.facilities[index].claimCurrency}
                                onChange={(e) => {
                                  formikProps?.setFieldValue(`facilities.${index}.claimCurrency`, e);
                                  searchRequest(e)
                                }}
                                searchList={searchList}
                                onFocus={() => {
                                  setSearchList([])
                                  searchRequest(formikProps.values &&
                                    formikProps.values.facilities[index].claimCurrency || ''
                                  )
                                }}
                                onSearchSelect={() => {
                                  setSearchList([]);
                                }}
                                component={SearchField}
                                isRequired
                                disabled={formikProps.values.lock}
                              />
                            </Col>

                            <Col lg={3} md={6} sm={6} xs={12}>
                              <Field
                                label='Claim Amount'
                                name={`facilities.${index}.claimAmount`}
                                value={formikProps.values[`facilities.${index}.claimAmount`]}
                                mask={maskHelper.getMask("currency", authContext.region, 2, 12, null, 0)}
                                onChange={(e) => {
                                  formikProps?.setFieldValue(`facilities.${index}.claimAmount`, e)
                                }}
                                component={InputField}
                                isRequired
                                disabled={formikProps.values.lock}
                                onBlur={() => { getPercLnSizeByFacility(false) }}
                              />
                            </Col>

                            <Col lg={3} md={6} sm={6} xs={12}>
                              <Field
                                label='Reserve Amount'
                                name={`facilities.${index}.reserveAmount`}
                                value={formikProps.values[`facilities.${index}.reserveAmount`]}
                                mask={maskHelper.getMask("currency", authContext.region, 2, 12, null, 0)}
                                onChange={(e) => {
                                  formikProps?.setFieldValue(`facilities.${index}.reserveAmount`, e)
                                }}
                                component={InputField}
                                disabled={formikProps.values.lock}
                              />
                            </Col>
                            <Col lg={3} md={6} sm={6} xs={12} />
                          </Row>
                        </>

                        <Row style={{ display: "flex", paddingBottom: "15px", justifyContent: "center", alignItems: "center" }}>
                          <Col>
                            <Text secundary></Text>
                          </Col>
                        </Row>
                        <Row style={{ marginLeft: "0px", marginRight: "0px", justifyContent: "center", alignItems: "center" }}>
                          {coluns ? coluns.map((colun, index) =>
                            <Col key={index}>
                              <Text style={{ margin: "0 auto" }}>
                                {colun.title}
                              </Text>
                            </Col>) : ""}
                        </Row>

                        <FieldArray name='insurers' render={() => (
                          <>
                            {facility.insurers.map((item, indexInsur) => (
                              <>
                                <Row key={indexInsur} style={{ display: "flex", marginLeft: "0px", marginRight: "0px", marginTop: 5, marginBottom: 5, justifyContent: "center", alignItems: "center" }}>
                                  <Col style={{ display: "flex", fontWeight: "bold", color: 'gray' }}>
                                    <Text>{item.legalEntityName || item.nmInsurer}</Text>
                                  </Col>
                                  <Col style={{ fontWeight: "bold" }}>
                                    {formikProps.values.lock ? (
                                      <Text style={{ whiteSpace: "nowrap" }}>
                                        {maskHelper.resolveMask(formikProps.values.facilities[index].insurers[indexInsur].percLineSize, maskHelper.getPercentage(authContext.region, 5, "100.00", "0", true))}
                                      </Text>
                                    ) : (
                                      <Field
                                        disabled={formikProps.values.lock || !formikProps.values.facilities[index].claimAmount}
                                        name={`facilities.${index}.insurers.${indexInsur}.percLineSize`}
                                        value={format(formikProps?.values?.facilities[index].facility?.insurers[indexInsur].percLineSize)}
                                        mask={maskHelper.getPercentage(authContext.region, 5, String(maxValuePerc.toFixed(5)))}
                                        onChange={e => {
                                          formikProps?.setFieldValue(`facilities.${index}.insurers.${indexInsur}.percLineSize`, e)
                                        }}
                                        component={InputField}
                                        onBlur={() => { getPercLnSizeByFacility(false) }}
                                        onFocus={() => getLineSizeMaxValue(facility, index, indexInsur)}
                                        validation={false}
                                        isRequired
                                      />
                                    )}
                                  </Col>
                                  <Col style={{ fontWeight: "bold" }}>
                                    {formikProps.values.lock ? (
                                      <Text style={{ whiteSpace: "nowrap", textAlign: 'right', display: "block" }}>{format(formikProps.values.facilities[index].insurers[indexInsur].amount)}</Text>
                                    ) : (
                                      <Field
                                        disabled={formikProps.values.lock || !formikProps.values.facilities[index].claimAmount}
                                        name={`facilities.${index}.insurers.${indexInsur}.amount`}
                                        value={format(formikProps.values.facilities[index].insurers[indexInsur].amount)}
                                        mask={maskHelper.getMask("currency", authContext.region, 2, 12, maxValueAmount, 0)}
                                        onChange={e => {
                                          formikProps?.setFieldValue(`facilities.${index}.insurers.${indexInsur}.amount`, e);
                                        }}
                                        component={InputField}
                                        validation={false}
                                        onBlur={() => getPercLineSizeByAmount()}
                                        onFocus={() => getAmountMaxValue(facility, index, indexInsur, facility.claimAmount)}
                                        isRequired
                                      />
                                    )}
                                  </Col>
                                  <Col>{item?.legalEntity?.lloydsType ?
                                    <Field
                                      disabled={formikProps.values.lock || operation?.idDomainStatus !== 250}
                                      name={`facilities.${index}.insurers.${indexInsur}.osnd`}
                                      value={format(formikProps.values?.facilities[index].insurers[indexInsur].osnd) || " "}
                                      onChange={e => {
                                        formikProps?.setFieldValue(`facilities.${index}.insurers.${indexInsur}.osnd`, e)
                                      }}
                                      component={InputField}
                                      validation={false}
                                      isRequired
                                    />
                                    : <Text style={{ whiteSpace: "nowrap" }}>N/A</Text>}
                                  </Col>
                                </Row>
                                <Line style={{ marginTop: 5 }} />
                              </>
                            ))}
                          </>
                        )} />

                        <Row style={{ display: "flex", marginLeft: "0px", marginRight: "0px", marginTop: 5, marginBottom: 5, justifyContent: "center", alignItems: "center" }}>
                          <Col style={{ display: "flex", textAlign: "center", justifyContent: "center", fontWeight: "bold", paddingTop: "10px", paddingBottom: "5px", color: "black" }}>
                            <Text style={{ whiteSpace: "nowrap" }}></Text>
                          </Col>
                          <Col style={{ marginLeft: "0px", justifyContent: "center", alignItems: "center", paddingTop: "10px", paddingBottom: "5px", color: "black" }}>
                            <Text style={{ whiteSpace: "nowrap" }}>{facility.percLineSizeTotal ? facility.percLineSizeTotal.toFixed(5) : 0}%</Text>
                          </Col>
                          <Col style={{ marginLeft: "0px", justifyContent: "center", alignItems: "center", paddingTop: "10px", paddingBottom: "5px", color: "black" }}>
                            <Text style={{ whiteSpace: "nowrap", textAlign: 'right', display: "block" }}>{format(facility.totalAmount)}</Text>
                          </Col>
                          <Col style={{ textAlign: "center", marginLeft: "0px", justifyContent: "center", alignItems: "center", paddingTop: "10px", paddingBottom: "5px", color: "black" }}>
                            <Text style={{ whiteSpace: "nowrap" }}></Text>
                          </Col>
                        </Row>
                        <Line style={{ marginBottom: 30 }} />
                      </FormContainer>)
                  })}

                  {!isBound && <Row style={{ marginTop: '2rem' }}>
                    <Col style={{ maxWidth: 210 }}>
                      <Label>
                        <strong style={{ fontWeight: 600, fontSize: 15 }}> Lock and save Line Size.</strong>
                      </Label>
                    </Col>
                    <Col>
                      <BorderSwitch>
                        <Field
                          component={Switch}
                          height={27}
                          width={55}
                          name='lock'
                          checked={formikProps.values.lock}
                          onChange={(a) => {
                            if (!a) return handleLock(a, formikProps.values);
                            const canLock = validateValues(formikProps.values.facilities);
                            if (canLock) {
                              validation.validate(formikProps.values).then(() => handleLock(a, formikProps.values)).catch((e) => {
                                if(e.type == "insurer-legal-entity"){
                                  mc.error('Insurer does not have a legal entity selected.')
                                }else{
                                  mc.error('Please fill in all required fields.')
                                }
                              });
                            } else {
                              mc.error('Please fill in all fields correctly!')
                            }
                          }}
                          checkedIcon={false}
                          uncheckedIcon={false}
                          handleDiameter={20}
                          offColor={Colors.WHITE}
                          onColor={Colors.GRAY[6]}
                          offHandleColor={Colors.GRAY[6]}
                          onHandleColor={Colors.WHITE}
                          disabled={isBound}
                        />
                      </BorderSwitch>
                    </Col>
                  </Row>}

                  <ButtonContainer>
                    <Button style={{ padding: "0.75rem 1rem" }} forth onClick={() => { history.goBack() }}>&lt; Back</Button>
                    <HasPermission permission={!isBound}>
                      <ButtonContent>
                        <LostCancelButton idOperation={props.match?.params?.idOperation} screen={"claims"}/>
                        {formikProps.values.lock && <>
                          {(shouldGenerateLpanFdo) && <Button
                            type="button"
                            primary
                            style={{ marginLeft: "10px", textAlign: "center" }}
                            onClick={() => generateFDOLpans()}
                          >
                            FDO LPAN
                          </Button>}

                          {(!shouldGenerateLpanFdo || (shouldGenerateLpanFdo && operation?.idDomainStatus === 250)) && <Button
                            primary
                            style={{ marginLeft: "10px" }}
                            onClick={() => {
                              setFacilities(formikProps.values.facilities);
                              validation.validate(formikProps.values).then(() => history.push(`/broking/payment-pack-configuration/${props.match?.params?.idOperation}`)).catch((e) => {
                                console.log('validation error', e)
                                mc.error('Please fill in all required fields.')
                              });
                            }
                            }
                          >
                            PAYMENT CONFIG
                          </Button>}
                        </>}
                      </ButtonContent>
                    </HasPermission>
                  </ButtonContainer>
                </Form>

                {loading.active && <Loader backdropColor={Colors.WHITE} fullScreen />}
              </ContentCard>
            </>)
        }}
      </Formik>
    </Layout>
  );
}
